import React, { useEffect, useState } from 'react';
import {
  Box,
  Menu,
  Drawer,
  useTheme,
  Avatar,
  Typography,
  Divider,
  IconButton,
  Stack,
  BoxProps,
  Chip,
  Tooltip,
  styled,
} from '@mui/material';

import { IconMail, IconUser } from '@tabler/icons-react';
import WbSunnyTwoToneIcon from '@mui/icons-material/WbSunnyTwoTone';
import DarkModeTwoToneIcon from '@mui/icons-material/DarkModeTwoTone';
import SwipeLeftAltTwoToneIcon from '@mui/icons-material/SwipeLeftAltTwoTone';
import SwipeRightAltTwoToneIcon from '@mui/icons-material/SwipeRightAltTwoTone';
import CallToActionTwoToneIcon from '@mui/icons-material/CallToActionTwoTone';
import { BorderOuter } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { RootState, useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';
import {
  AdminDto,
  useAdminMeControllerAdminMeQuery,
  UserDto,
  useUserMeControllerUserMeQuery,
} from '@shared/services/apiService/apiService';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { DesignAccountPanel } from '@shared/designVariants/DesignAccountPanel';

const Profile = ({ drawer = false }: { drawer?: boolean }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const pathWithoutLastPart = pathname.slice(0, pathname.lastIndexOf('/'));
  const isSuperAdminPage = pathWithoutLastPart.includes('super-admin');

  const { t } = useTranslation();

  const [anchorEl2, setAnchorEl2] = useState(null);

  const customizer = useAppSelector((state: RootState) => state.customizer);
  const authenticatedUser = useAppSelector((state: RootState) => state.auth?.authenticatedUser);

  const useMe = isSuperAdminPage
    ? useAdminMeControllerAdminMeQuery
    : useUserMeControllerUserMeQuery;

  const { data, refetch } = useMe();

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handleRefreshUser = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'call-agent:user-role-updated-at' || event.key === 'accessToken') {
        refetch();
      }
    };

    window.addEventListener('focus', handleRefreshUser);
    document.addEventListener('visibilitychange', handleRefreshUser);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('focus', handleRefreshUser);
      document.removeEventListener('visibilitychange', handleRefreshUser);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refetch]);

  useEffect(() => {
    if (!isSuperAdminPage && data) {
      dispatch(actions.auth.setAuthenticatedUser(data as UserDto));
    }
  }, [data, dispatch, isSuperAdminPage]);

  const handleClick2 = (event: any) => {
    setAnchorEl2(event.currentTarget);
    refetch();
  };

  const handleClose2 = () => {
    setAnchorEl2(null);
  };

  const currentUser = (data as unknown as UserDto | undefined) ?? authenticatedUser;
  const isViewerRole = String(currentUser?.role ?? '').toLowerCase() === UserRolesEnum.VIEWER;

  const StyledBox = styled(Box)<BoxProps>(({ theme }) => ({
    boxShadow: theme.shadows[8],
    padding: '20px',
    cursor: 'pointer',
    justifyContent: 'center',
    display: 'flex',
    transition: '0.1s ease-in',
    border: `1px solid ${theme.palette.border.subtle}`,
    '&:hover': {
      transform: 'scale(1.05)',
    },
  }));

  return (
    <Box>
      <IconButton
        data-testid="profile-menu-button"
        size="large"
        aria-label="Open account"
        color="inherit"
        aria-controls={drawer ? 'design-account-panel' : 'msgs-menu'}
        aria-haspopup={drawer ? 'dialog' : 'menu'}
        aria-expanded={Boolean(anchorEl2)}
        sx={{
          ...(typeof anchorEl2 === 'object' && {
            color: 'primary.main',
          }),
        }}
        onClick={handleClick2}>
        <Avatar
          aria-label={t('common.profile', { defaultValue: 'Profile' })}
          sx={(theme) => ({
            width: 35,
            height: 35,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[200],
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.secondary
                : theme.palette.grey[500],
          })}>
          <IconUser size={18} stroke={1.5} />
        </Avatar>
      </IconButton>
      {drawer ? (
        <Drawer
          anchor={theme.direction === 'rtl' ? 'left' : 'right'}
          open={Boolean(anchorEl2)}
          onClose={handleClose2}
          PaperProps={{
            id: 'design-account-panel',
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': 'design-account-title',
            sx: { width: 360, maxWidth: '100vw' },
          }}>
          <DesignAccountPanel user={currentUser} onClose={handleClose2} />
        </Drawer>
      ) : (
        <Menu
          id="msgs-menu"
          anchorEl={anchorEl2}
          keepMounted
          open={Boolean(anchorEl2)}
          onClose={handleClose2}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          sx={{
            '& .MuiMenu-paper': {
              width: '360px',
              p: 4,
            },
          }}>
          <Typography variant="h4">User Profile</Typography>
          <Box py={3}>
            <Stack direction="row" gap={2} alignItems="center">
              <Box>
                {(data as UserDto | AdminDto)?.role === 'manager' ? (
                  <>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      fontSize="16px"
                      fontWeight={600}>
                      {t('superAdminMainPage.profile.superAdmin')}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      display="flex"
                      alignItems="center"
                      gap={1}>
                      <IconMail width={15} height={15} />
                      {data?.email}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap" mb={0.5}>
                      <Typography
                        component="span"
                        variant="subtitle2"
                        color="textPrimary"
                        fontSize="16px"
                        fontWeight={600}>
                        {currentUser?.firstName} {currentUser?.lastName}
                      </Typography>
                      <Tooltip
                        title={isViewerRole ? t('common.viewerModeTooltip') : ''}
                        arrow
                        disableHoverListener={!isViewerRole}>
                        <Chip
                          data-testid="user-role-label"
                          size="small"
                          label={isViewerRole ? t('common.viewerMode') : 'Admin'}
                          sx={(theme) => ({
                            height: '23px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: isViewerRole
                              ? theme.palette.grey[200]
                              : theme.palette.primary.light,
                            color: isViewerRole
                              ? theme.palette.text.secondary
                              : theme.palette.primary.main,
                            '.MuiChip-label': {
                              padding: '0 10px',
                              lineHeight: 'normal',
                            },
                          })}
                        />
                      </Tooltip>
                    </Stack>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      display="flex"
                      alignItems="center"
                      gap={1}>
                      <IconMail width={15} height={15} />
                      {currentUser?.email}
                    </Typography>
                  </>
                )}
              </Box>
            </Stack>
          </Box>
          <Divider />
          <Typography mt={2} variant="h4">
            Settings
          </Typography>
          <Box pt={3}>
            <Typography variant="h6" gutterBottom>
              Theme Option
            </Typography>
            <Stack direction={'row'} gap={2} my={2}>
              <StyledBox
                onClick={() => dispatch(actions.customizer.setDarkMode('light'))}
                display="flex"
                gap={1}>
                <WbSunnyTwoToneIcon
                  color={customizer.activeMode === 'light' ? 'primary' : 'inherit'}
                />
                Light
              </StyledBox>
              <StyledBox
                onClick={() => dispatch(actions.customizer.setDarkMode('dark'))}
                display="flex"
                gap={1}>
                <DarkModeTwoToneIcon
                  color={customizer.activeMode === 'dark' ? 'primary' : 'inherit'}
                />
                Dark
              </StyledBox>
            </Stack>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Theme Direction
            </Typography>
            <Stack direction={'row'} gap={2} my={2}>
              <StyledBox
                onClick={() => dispatch(actions.customizer.setDir('ltr'))}
                display="flex"
                gap={1}>
                <SwipeLeftAltTwoToneIcon
                  color={customizer.activeDir === 'ltr' ? 'primary' : 'inherit'}
                />{' '}
                LTR
              </StyledBox>
              <StyledBox
                onClick={() => dispatch(actions.customizer.setDir('rtl'))}
                display="flex"
                gap={1}>
                <SwipeRightAltTwoToneIcon
                  color={customizer.activeDir === 'rtl' ? 'primary' : 'inherit'}
                />{' '}
                RTL
              </StyledBox>
            </Stack>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Card With
            </Typography>
            <Stack direction={'row'} gap={2} my={2}>
              <StyledBox
                onClick={() => dispatch(actions.customizer.setCardShadow(false))}
                display="flex"
                gap={1}>
                <BorderOuter color={!customizer.isCardShadow ? 'primary' : 'inherit'} />
                Border
              </StyledBox>
              <StyledBox
                onClick={() => dispatch(actions.customizer.setCardShadow(true))}
                display="flex"
                gap={1}>
                <CallToActionTwoToneIcon color={customizer.isCardShadow ? 'primary' : 'inherit'} />
                Shadow
              </StyledBox>
            </Stack>
          </Box>
        </Menu>
      )}
    </Box>
  );
};

export default Profile;
